// Pure helpers for the developer portal: manifest parsing + validation errors,
// version/listing/delivery status labels, the profile form and earnings. No
// React/store imports.
import { FEE_SCHEDULE, REVIEW_RULES, validateManifest, type AnyManifest } from '~/lib/plugin-sdk';
import type {
  CheckFinding,
  CheckReport,
  CreatorReferralView,
  DeliveryView,
  DevListingView,
  DeveloperView,
  EarningStreamId,
  EarningsView,
  ExpertRequestView,
  ExpertSelfView,
  ExpertService,
  ListingAnalytics,
  ReviewStatus,
  VersionStatus,
  VersionView,
} from '@dunea/shared';
import type { Tone } from '~/components/workbench/tools/payments/payments-model';

export { apiErrorMessage } from '~/components/workbench/tools/payments/payments-model';
export { formatUsd } from '~/components/workbench/tools/marketplace/marketplace-model';

export const DOCS_URL = 'https://docs.dunea.ai/developers/overview';
export const DOCS_QUICKSTART_URL = 'https://docs.dunea.ai/developers/quickstart-plugin';
export const DOCS_THEME_URL = 'https://docs.dunea.ai/developers/quickstart-theme';

/** Mirrors packages/plugin-sdk/README.md "Quickstart". */
export const CLI_QUICKSTART = [
  'npx @dunea/plugin-sdk init my-plugin',
  'cd my-plugin',
  'npx dunea-plugin validate',
  'export DUNEA_DEV_TOKEN=dpk_…   # create one below',
  'npx dunea-plugin publish --submit',
] as const;

// ─── manifest input ───

export interface ManifestCheck {
  ok: boolean;
  manifest: AnyManifest | null;
  /** The parsed JSON as typed (sent as-is: the server re-validates). */
  raw: unknown;
  errors: string[];
}

/** Parse pasted/uploaded JSON and validate it exactly like the marketplace does. */
export function checkManifestText(text: string): ManifestCheck {
  if (!text.trim()) {
    return { ok: false, manifest: null, raw: null, errors: ['Paste or upload a dunea manifest (JSON).'] };
  }

  let raw: unknown;

  try {
    raw = JSON.parse(text);
  } catch (err) {
    return {
      ok: false,
      manifest: null,
      raw: null,
      errors: [`JSON: ${err instanceof Error ? err.message : 'not valid JSON'}`],
    };
  }

  const result = validateManifest(raw);

  return { ok: result.ok, manifest: result.manifest ?? null, raw, errors: result.errors };
}

export interface ErrorGroup {
  /** "(root)", "developer.supportEmail", "agentTools.0.name"… */
  path: string;
  messages: string[];
}

/** "path: message" lines (validateManifest's format) grouped by path, in first-seen order. */
export function groupManifestErrors(errors: readonly string[]): ErrorGroup[] {
  const groups = new Map<string, string[]>();

  for (const line of errors) {
    const idx = line.indexOf(': ');
    const path = idx > 0 ? line.slice(0, idx) : '(root)';
    const message = idx > 0 ? line.slice(idx + 2) : line;
    const list = groups.get(path) ?? [];

    if (!list.includes(message)) {
      list.push(message);
    }

    groups.set(path, list);
  }

  return Array.from(groups, ([path, messages]) => ({ path, messages }));
}

/** A new version's manifest must be for this listing and a version not uploaded yet. */
export function versionManifestErrors(
  manifest: AnyManifest | null,
  listing: Pick<DevListingView, 'slug' | 'kind' | 'versions'>,
): string[] {
  if (!manifest) {
    return [];
  }

  const errors: string[] = [];

  if (manifest.id !== listing.slug) {
    errors.push(`id: must be "${listing.slug}" (this listing), got "${manifest.id}"`);
  }

  if (manifest.kind !== listing.kind) {
    errors.push(`kind: this listing is a ${listing.kind}`);
  }

  if (listing.versions.some((v) => v.version === manifest.version)) {
    errors.push(`version: ${manifest.version} already exists. Bump it.`);
  }

  return errors;
}

// ─── statuses ───

const VERSION_META: Record<VersionStatus, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'muted' },
  in_review: { label: 'In review', tone: 'info' },
  approved: { label: 'Approved', tone: 'success' },
  rejected: { label: 'Rejected', tone: 'danger' },
  superseded: { label: 'Superseded', tone: 'muted' },
};

export function versionStatusMeta(status: string): { label: string; tone: Tone } {
  return VERSION_META[status as VersionStatus] ?? { label: status, tone: 'muted' };
}

const LISTING_META: Record<DevListingView['status'], { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'muted' },
  listed: { label: 'Listed', tone: 'success' },
  delisted: { label: 'Delisted', tone: 'warning' },
  suspended: { label: 'Suspended', tone: 'danger' },
};

export function listingStatusMeta(status: string): { label: string; tone: Tone } {
  return LISTING_META[status as DevListingView['status']] ?? { label: status, tone: 'muted' };
}

const DELIVERY_META: Record<DeliveryView['status'], { label: string; tone: Tone }> = {
  pending: { label: 'Pending', tone: 'info' },
  delivered: { label: 'Delivered', tone: 'success' },
  failed: { label: 'Retrying', tone: 'warning' },
  dead: { label: 'Failed', tone: 'danger' },
};

export function deliveryStatusMeta(status: string): { label: string; tone: Tone } {
  return DELIVERY_META[status as DeliveryView['status']] ?? { label: status, tone: 'muted' };
}

export const DELIVERY_FILTERS = [
  ['', 'All'],
  ['failed', 'Retrying'],
  ['dead', 'Failed'],
  ['delivered', 'Delivered'],
] as const;

const PAYOUTS_META: Record<DeveloperView['payoutsStatus'], { label: string; tone: Tone }> = {
  none: { label: 'Not set up', tone: 'muted' },
  pending: { label: 'Pending', tone: 'warning' },
  active: { label: 'Active', tone: 'success' },
  restricted: { label: 'Action needed', tone: 'danger' },
};

export function payoutsStatusMeta(status: string): { label: string; tone: Tone } {
  return PAYOUTS_META[status as DeveloperView['payoutsStatus']] ?? { label: status, tone: 'muted' };
}

/** Newest first by submitted (else reviewed) time; never-submitted drafts on top. */
export function sortVersions(versions: readonly VersionView[]): VersionView[] {
  const time = (v: VersionView) => Date.parse(v.submittedAt ?? v.reviewedAt ?? '') || Number.POSITIVE_INFINITY;
  return [...versions].sort((a, b) => time(b) - time(a));
}

/** An approved version that isn't the one installs get can be released again (rollback). */
export function canReleaseVersion(version: Pick<VersionView, 'status' | 'current'>): boolean {
  return version.status === 'approved' && !version.current;
}

export function reviewModeLabel(mode: VersionView['reviewMode']): string | null {
  return mode === 'auto' ? 'Auto-approved' : mode === 'human' ? 'Human review' : null;
}

/** "3rd in the queue · first response within 2 business days", or null when nothing is queued. */
export function reviewStatusLine(status: ReviewStatus | null | undefined): string | null {
  if (!status || status.status !== 'in_review') {
    return null;
  }

  const days = status.targetBusinessDays === 1 ? '1 business day' : `${status.targetBusinessDays} business days`;
  const target = `first response within ${days}`;

  if (status.queuePosition === null) {
    return `v${status.version} is in review · ${target}`;
  }

  return `v${status.version} is ${ordinal(status.queuePosition)} in the queue · ${target}`;
}

export function ordinal(n: number): string {
  const mod100 = n % 100;
  const suffix =
    mod100 >= 11 && mod100 <= 13 ? 'th' : (({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th');

  return `${n}${suffix}`;
}

/** A draft can be submitted; nothing else can. */
export function canSubmitVersion(version: Pick<VersionView, 'status'>): boolean {
  return version.status === 'draft';
}

/** The headline version for a listing row: in review > newest approved > newest. */
export function headlineVersion(versions: readonly VersionView[]): VersionView | null {
  const sorted = sortVersions(versions);
  return (
    sorted.find((v) => v.status === 'in_review') ?? sorted.find((v) => v.status === 'approved') ?? sorted[0] ?? null
  );
}

// ─── profile ───

export interface ProfileDraft {
  handle: string;
  displayName: string;
  supportEmail: string;
  website: string;
  bio: string;
}

const HANDLE = /^[a-z][a-z0-9-]{2,39}$/;

export function profileErrors(draft: ProfileDraft): Partial<Record<keyof ProfileDraft, string>> {
  const errors: Partial<Record<keyof ProfileDraft, string>> = {};

  if (!HANDLE.test(draft.handle.trim())) {
    errors.handle = 'Lowercase letters, digits and dashes, 3–40 characters, starting with a letter.';
  }

  if (draft.displayName.trim().length < 2) {
    errors.displayName = 'At least 2 characters.';
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.supportEmail.trim())) {
    errors.supportEmail = 'Enter the email buyers can reach you at.';
  }

  if (draft.website.trim() && !/^https:\/\/\S+$/.test(draft.website.trim())) {
    errors.website = 'Must start with https://';
  }

  if (draft.bio.length > 500) {
    errors.bio = 'Keep it under 500 characters.';
  }

  return errors;
}

/** "My Studio" → "my-studio" (a starting suggestion for the handle). */
export function suggestHandle(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^[^a-z]+/, '')
    .replace(/-+$/, '')
    .slice(0, 40);
}

// ─── server checks (POST dev/check) ───

export interface CheckGroup {
  rule: string;
  /** The REVIEW_RULES text for the id, when it's a known rule. */
  ruleText: string | null;
  level: 'error' | 'warning';
  findings: { message: string; path: string | null }[];
}

export function ruleText(id: string): string | null {
  return (REVIEW_RULES as Record<string, string>)[id] ?? null;
}

/** Findings grouped by rule id: errors first, then warnings; first-seen order within each. */
export function groupCheckFindings(report: Pick<CheckReport, 'errors' | 'warnings'> | null | undefined): CheckGroup[] {
  if (!report) {
    return [];
  }

  const groups = new Map<string, CheckGroup>();
  const add = (f: CheckFinding, level: 'error' | 'warning') => {
    const rule = f.rule || 'other';
    const key = `${level}:${rule}`;
    const group = groups.get(key) ?? { rule, ruleText: ruleText(rule), level, findings: [] };

    if (!group.findings.some((x) => x.message === f.message && x.path === (f.path ?? null))) {
      group.findings.push({ message: f.message, path: f.path ?? null });
    }

    groups.set(key, group);
  };

  for (const f of report.errors ?? []) {
    add(f, 'error');
  }

  for (const f of report.warnings ?? []) {
    add(f, 'warning');
  }

  return Array.from(groups.values());
}

export function checkSummary(report: Pick<CheckReport, 'ok' | 'errors' | 'warnings'>): string {
  const e = report.errors.length;
  const w = report.warnings.length;
  const plural = (n: number, one: string) => `${n} ${n === 1 ? one : `${one}s`}`;

  if (report.ok && w === 0) {
    return 'All checks passed.';
  }

  if (report.ok) {
    return `Ready to submit, with ${plural(w, 'warning')}.`;
  }

  return w ? `${plural(e, 'error')} and ${plural(w, 'warning')}.` : `${plural(e, 'error')} to fix.`;
}

// ─── analytics ───

export interface Bar {
  date: string;
  value: number;
  /** 0–1 of the window's max (a zero window stays flat). */
  height: number;
}

type DailyKey = 'views' | 'installs' | 'uninstalls' | 'netRevenue';

/** Daily values for one series, scaled for a div bar chart. */
export function analyticsBars(daily: ListingAnalytics['daily'] | null | undefined, key: DailyKey): Bar[] {
  const rows = daily ?? [];
  const max = rows.reduce((m, r) => Math.max(m, Number(r[key]) || 0), 0);

  return rows.map((r) => {
    const value = Math.max(0, Number(r[key]) || 0);
    return { date: r.date, value, height: max > 0 ? value / max : 0 };
  });
}

/** 0.0412 → "4.1%". */
export function percent(ratio: number, digits = 1): string {
  if (!Number.isFinite(ratio) || ratio <= 0) {
    return '0%';
  }

  const value = Math.round(Math.min(1, ratio) * 100 * 10 ** digits) / 10 ** digits;

  return `${value}%`;
}

export const CHURN_BUCKETS = [
  ['sameDay', 'Same day'],
  ['within14Days', 'Within 14 days'],
  ['within90Days', 'Within 90 days'],
  ['later', 'Later'],
] as const;

/** Uninstall buckets with their share of all uninstalls in the window. */
export function churnRows(
  churn: ListingAnalytics['churn'] | null | undefined,
): { label: string; count: number; share: number }[] {
  const c = churn ?? { sameDay: 0, within14Days: 0, within90Days: 0, later: 0 };
  const total = CHURN_BUCKETS.reduce((sum, [k]) => sum + Math.max(0, c[k] || 0), 0);

  return CHURN_BUCKETS.map(([k, label]) => {
    const count = Math.max(0, c[k] || 0);
    return { label, count, share: total > 0 ? count / total : 0 };
  });
}

/** Sources by views, biggest first, each with its own install conversion. */
export function sourceRows(sources: ListingAnalytics['sources'] | null | undefined) {
  const rows = [...(sources ?? [])].sort((a, b) => b.views - a.views || b.installs - a.installs);
  const max = rows.reduce((m, r) => Math.max(m, r.views), 0);

  return rows.map((r) => ({
    source: r.source || 'direct',
    views: r.views,
    installs: r.installs,
    conversion: r.views > 0 ? r.installs / r.views : 0,
    width: max > 0 ? r.views / max : 0,
  }));
}

// ─── earnings ───

export const STREAM_LABELS: Record<EarningStreamId, string> = {
  sale: 'Sales',
  subscription: 'Subscriptions',
  usage: 'Usage',
  referral: 'Referrals',
  services: 'Services',
};

export const STREAM_ORDER: EarningStreamId[] = ['sale', 'subscription', 'usage', 'referral', 'services'];

/** Net by stream in a fixed order; streams with nothing are kept so the list doesn't jump. */
export function streamRows(byStream: Partial<EarningsView['byStream']> | null | undefined) {
  return STREAM_ORDER.map((id) => ({ id, label: STREAM_LABELS[id], net: byStream?.[id] ?? 0 }));
}

/** How much of the founder allowance is used, 0–1. */
export function allowanceUsed(remainingCents: number): number {
  const total = FEE_SCHEDULE.founderAllowanceCents;
  return total > 0 ? Math.min(1, Math.max(0, (total - Math.max(0, remainingCents)) / total)) : 1;
}

function usdWhole(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

function pct(bps: number): string {
  const n = bps / 100;
  return Number.isInteger(n) ? `${n}%` : `${n.toFixed(1)}%`;
}

/**
 * The founding terms in one sentence, from FEE_SCHEDULE:
 * "Keep 100% of your first $25,000 in sales. After that Dunea takes 10–15%."
 */
export function founderTermsSentence(
  schedule: {
    founderAllowanceCents: number;
    feeBpsByKind: Record<string, number>;
    usageFeeBps: number;
  } = FEE_SCHEDULE,
): string {
  const fees = [...Object.values(schedule.feeBpsByKind), schedule.usageFeeBps].filter((n) => Number.isFinite(n));
  const lo = Math.min(...fees);
  const hi = Math.max(...fees);
  const range = lo === hi ? pct(lo) : `${pct(lo).replace('%', '')}–${pct(hi)}`;

  return `Keep 100% of your first ${usdWhole(schedule.founderAllowanceCents)} in sales. After that Dunea takes ${range}.`;
}

/** Fee by listing kind after the allowance, for the earnings card. */
export function feeByKindLine(schedule = FEE_SCHEDULE): string {
  const kinds = Object.entries(schedule.feeBpsByKind) as [string, number][];
  return kinds.map(([kind, bps]) => `${kind}s ${pct(bps)}`).join(', ');
}

// ─── referrals ───

/** "25% of what they spend for 12 months". */
export function referralTerms(ref: Pick<CreatorReferralView, 'shareBps' | 'months'>): string {
  return `${pct(ref.shareBps)} of what they spend for ${ref.months === 1 ? '1 month' : `${ref.months} months`}`;
}

// ─── experts ───

const EXPERT_STATUS: Record<ExpertSelfView['status'], { label: string; tone: Tone }> = {
  pending: { label: 'Pending review', tone: 'warning' },
  approved: { label: 'Listed', tone: 'success' },
  paused: { label: 'Paused', tone: 'muted' },
  rejected: { label: 'Not approved', tone: 'danger' },
};

export function expertStatusMeta(status: string): { label: string; tone: Tone } {
  return EXPERT_STATUS[status as ExpertSelfView['status']] ?? { label: status, tone: 'muted' };
}

const REQUEST_STATUS: Record<ExpertRequestView['status'], { label: string; tone: Tone }> = {
  new: { label: 'New', tone: 'info' },
  accepted: { label: 'Accepted', tone: 'accent' },
  declined: { label: 'Declined', tone: 'muted' },
  completed: { label: 'Completed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
};

export function requestStatusMeta(status: string): { label: string; tone: Tone } {
  return REQUEST_STATUS[status as ExpertRequestView['status']] ?? { label: status, tone: 'muted' };
}

/** What the expert can do with a request in its current state. */
export function requestActions(status: ExpertRequestView['status']): ('accept' | 'decline' | 'complete')[] {
  if (status === 'new') {
    return ['accept', 'decline'];
  }

  return status === 'accepted' ? ['complete'] : [];
}

export const EXPERT_CURRENCIES = ['USD', 'GBP', 'INR', 'AED'] as const;

export interface ServiceDraft {
  title: string;
  description: string;
  /** Major units as typed. */
  price: string;
  currency: ExpertService['currency'];
  deliveryDays: string;
}

export interface ExpertDraft {
  headline: string;
  about: string;
  services: ServiceDraft[];
  /** Comma separated, as typed. */
  countries: string;
  languages: string;
  /** One URL per line. */
  portfolio: string;
  acceptingWork: boolean;
}

export function emptyService(): ServiceDraft {
  return { title: '', description: '', price: '', currency: 'USD', deliveryDays: '7' };
}

export function expertDraftFrom(self: ExpertSelfView | null | undefined): ExpertDraft {
  if (!self) {
    return {
      headline: '',
      about: '',
      services: [emptyService()],
      countries: '',
      languages: 'English',
      portfolio: '',
      acceptingWork: true,
    };
  }

  return {
    headline: self.headline,
    about: self.about,
    services: self.services.length
      ? self.services.map((s) => ({
          title: s.title,
          description: s.description,
          price: String(s.priceFromCents / 100),
          currency: s.currency,
          deliveryDays: String(s.deliveryDays),
        }))
      : [emptyService()],
    countries: self.countries.join(', '),
    languages: self.languages.join(', '),
    portfolio: self.portfolio.join('\n'),
    acceptingWork: self.acceptingWork,
  };
}

function list(text: string, sep: RegExp): string[] {
  return Array.from(
    new Set(
      text
        .split(sep)
        .map((x) => x.trim())
        .filter(Boolean),
    ),
  );
}

export function expertDraftErrors(draft: ExpertDraft): string[] {
  const errors: string[] = [];

  if (draft.headline.trim().length < 10) {
    errors.push('Headline: at least 10 characters.');
  }

  if (draft.about.trim().length < 40) {
    errors.push('About: at least 40 characters.');
  }

  const services = draft.services.filter((s) => s.title.trim() || s.description.trim() || s.price.trim());

  if (services.length === 0) {
    errors.push('Add at least one service.');
  }

  services.forEach((s, i) => {
    const n = i + 1;

    if (s.title.trim().length < 3) {
      errors.push(`Service ${n}: add a title.`);
    }

    if (!/^\d+(\.\d{1,2})?$/.test(s.price.trim()) || Number(s.price) <= 0) {
      errors.push(`Service ${n}: enter a starting price.`);
    }

    if (!/^\d+$/.test(s.deliveryDays.trim()) || Number(s.deliveryDays) < 1 || Number(s.deliveryDays) > 365) {
      errors.push(`Service ${n}: delivery days from 1 to 365.`);
    }
  });

  const countries = list(draft.countries, /[,\s]+/);

  if (countries.length === 0 || countries.some((c) => !/^[A-Za-z]{2}$/.test(c))) {
    errors.push('Countries: two-letter codes, like US, GB, IN.');
  }

  if (list(draft.languages, /,/).length === 0) {
    errors.push('Languages: add at least one.');
  }

  const bad = list(draft.portfolio, /\s+/).filter((u) => !/^https:\/\/\S+$/.test(u));

  if (bad.length) {
    errors.push('Portfolio: links must start with https://');
  }

  return errors;
}

/** The PUT body. Assumes expertDraftErrors() passed. */
export function expertPayload(draft: ExpertDraft) {
  return {
    headline: draft.headline.trim(),
    about: draft.about.trim(),
    services: draft.services
      .filter((s) => s.title.trim())
      .map<ExpertService>((s) => ({
        title: s.title.trim(),
        description: s.description.trim(),
        priceFromCents: Math.round(Number(s.price) * 100),
        currency: s.currency,
        deliveryDays: Number(s.deliveryDays),
      })),
    countries: list(draft.countries, /[,\s]+/).map((c) => c.toUpperCase()),
    languages: list(draft.languages, /,/),
    portfolio: list(draft.portfolio, /\s+/),
    acceptingWork: draft.acceptingWork,
  };
}

/** Platform fee as a share of gross, e.g. "20%". */
export function feeShare(gross: number, fee: number): string {
  if (!gross) {
    return '—';
  }

  return `${Math.round((fee / gross) * 100)}%`;
}
